import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  List,
  Modal,
  Row,
  Spin,
  Statistic,
  Tag,
  Typography,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import PaymentStatus from "../NewResume/PaymentStatus";
import { buildDownloadPurchaseContext } from "../NewResume/paymentAccess";
import useSubscriptionStatus from "./useSubscriptionStatus";

const { Title, Paragraph, Text } = Typography;

function formatDate(value) {
  if (!value) return "Not available";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export default function SubscriptionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const {
    subscription,
    payments,
    loading,
    refreshAll,
  } = useSubscriptionStatus();

  useEffect(() => {
    refreshAll().catch((error) => {
      messageApi.error(error.message || "Unable to load subscription details.");
    });
  }, [messageApi, refreshAll]);

  const expired = subscription?.status === "expired";
  const active = Boolean(subscription?.active);
  const statusColor = active ? "green" : expired ? "red" : "default";
  const query = new URLSearchParams(location.search);
  const returnTo = query.get("returnTo");
  const safeReturnTo =
    returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//")
      ? returnTo
      : null;

  return (
    <main style={{ padding: "32px 20px", maxWidth: 1080, margin: "0 auto" }}>
      {contextHolder}
      <Spin spinning={loading}>
        <Row gutter={[20, 20]}>
          <Col span={24}>
            <Title level={2} style={{ marginBottom: 4 }}>
              ResumeIQ Premium
            </Title>
            <Paragraph type="secondary">
              Explore every template for free. Subscribe for seven days of
              unlimited PDF, DOCX, print, AI, and resume exports.
            </Paragraph>
          </Col>

          {expired ? (
            <Col span={24}>
              <Alert
                type="error"
                showIcon
                message="Your 7-day subscription has expired. Renew for ₹100 to continue using premium features."
              />
            </Col>
          ) : null}

          <Col xs={24} lg={16}>
            <Card>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 16,
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <div>
                  <Text type="secondary">Current plan</Text>
                  <Title level={3} style={{ margin: 0 }}>
                    ResumeIQ 7-Day Premium
                  </Title>
                </div>
                <Badge
                  status={active ? "success" : expired ? "error" : "default"}
                  text={
                    <Tag color={statusColor}>
                      {active ? "Active" : expired ? "Expired" : "Inactive"}
                    </Tag>
                  }
                />
              </div>

              <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
                <Descriptions.Item label="Price">₹100 INR</Descriptions.Item>
                <Descriptions.Item label="Duration">7 days</Descriptions.Item>
                <Descriptions.Item label="Start date">
                  {formatDate(subscription?.subscriptionStartAt)}
                </Descriptions.Item>
                <Descriptions.Item label="Expiry date">
                  {formatDate(subscription?.subscriptionExpiresAt)}
                </Descriptions.Item>
                <Descriptions.Item label="Payment status">
                  {subscription?.paymentStatus || "unpaid"}
                </Descriptions.Item>
                <Descriptions.Item label="Transaction ID">
                  {subscription?.transactionId || "Not available"}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card style={{ height: "100%" }}>
              <Statistic
                title="Days remaining"
                value={subscription?.daysRemaining || 0}
                suffix="days"
              />
              <Button
                type="primary"
                block
                icon={<ReloadOutlined />}
                style={{ marginTop: 24 }}
                onClick={() => setPaymentOpen(true)}
              >
                {active ? "Renew for ₹100" : "Subscribe for ₹100"}
              </Button>
            </Card>
          </Col>

          <Col span={24}>
            <Card title="Payment history">
              {payments.length ? (
                <List
                  dataSource={payments}
                  renderItem={(payment) => (
                    <List.Item>
                      <List.Item.Meta
                        title={`${payment.status === "paid" ? "Paid" : payment.status} · ₹${payment.amountInr}`}
                        description={`${formatDate(payment.paidAt)} · ${payment.transactionId}`}
                      />
                      <Text type="secondary">
                        Expires {formatDate(payment.subscriptionExpiresAt)}
                      </Text>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty description="No payments yet" />
              )}
            </Card>
          </Col>
        </Row>
      </Spin>

      <Modal
        open={paymentOpen}
        footer={null}
        centered
        title="ResumeIQ 7-Day Premium · ₹100"
        onCancel={() => setPaymentOpen(false)}
      >
        <PaymentStatus
          setPaymentOpen={setPaymentOpen}
          downloadLabel="premium access"
          purchaseContext={buildDownloadPurchaseContext({
            userName: sessionStorage.getItem("userName") || "",
            source: safeReturnTo || "/subscription",
          })}
          onPaymentSuccess={async () => {
            await refreshAll({ silent: true });
            if (safeReturnTo) {
              navigate(safeReturnTo, {
                replace: true,
                state: {
                  resumePremiumAction: query.get("intent") || null,
                },
              });
            }
            return { ok: true };
          }}
        />
      </Modal>
    </main>
  );
}
